import { RotateCw, X, Locate, LocateOff, Loader2, Car, PersonStanding } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDistance, formatTime, formatETA, calculateETA } from '../lib/distance';
import type { CostingModel } from '@/api/geo';

interface BottomControlPanelProps {
  remainingDistance: number; // meters
  remainingTime: number; // seconds
  costing: CostingModel;
  onCostingChange: (costing: CostingModel) => void;
  onReroute: () => void;
  onStop: () => void;
  onToggleFollow: () => void;
  isRerouting: boolean;
  followUser: boolean;
}

export function BottomControlPanel({
  remainingDistance,
  remainingTime,
  costing,
  onCostingChange,
  onReroute,
  onStop,
  onToggleFollow,
  isRerouting,
  followUser,
}: BottomControlPanelProps) {
  const eta = calculateETA(remainingTime);

  return (
    <div className="absolute bottom-0 left-0 right-0 z-10 safe-area-inset-bottom">
      <div className="bg-background/95 backdrop-blur-sm shadow-lg m-3 rounded-xl border">
        {/* Stats row */}
        <div className="grid grid-cols-3 divide-x border-b">
          <div className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Прибытие</p>
            <p className="text-lg font-bold">{formatETA(eta)}</p>
          </div>
          <div className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Расстояние</p>
            <p className="text-lg font-bold">{formatDistance(remainingDistance)}</p>
          </div>
          <div className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Время</p>
            <p className="text-lg font-bold">{formatTime(remainingTime)}</p>
          </div>
        </div>

        {/* Buttons row */}
        <div className="flex items-center gap-2 p-3">
          {/* Follow toggle */}
          <Button
            size="icon"
            variant={followUser ? 'default' : 'outline'}
            onClick={onToggleFollow}
            className="h-11 w-11 shrink-0"
          >
            {followUser ? (
              <Locate className="w-5 h-5" />
            ) : (
              <LocateOff className="w-5 h-5" />
            )}
          </Button>

          {/* Mode selector */}
          <div className="flex rounded-lg border overflow-hidden shrink-0">
            <Button
              size="icon"
              variant={costing === 'auto' ? 'default' : 'ghost'}
              onClick={() => onCostingChange('auto')}
              className="h-11 w-11 rounded-none"
              title="На машине"
            >
              <Car className="w-5 h-5" />
            </Button>
            <Button
              size="icon"
              variant={costing === 'pedestrian' ? 'default' : 'ghost'}
              onClick={() => onCostingChange('pedestrian')}
              className="h-11 w-11 rounded-none"
              title="Пешком"
            >
              <PersonStanding className="w-5 h-5" />
            </Button>
          </div>

          {/* Reroute button */}
          <Button
            size="icon"
            variant="outline"
            className="h-11 w-11 shrink-0"
            onClick={onReroute}
            disabled={isRerouting}
            title="Перепроложить"
          >
            {isRerouting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <RotateCw className="w-5 h-5" />
            )}
          </Button>

          {/* Stop button */}
          <Button
            size="icon"
            variant="destructive"
            className="h-11 w-11 shrink-0"
            onClick={onStop}
            title="Завершить"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
